import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FileService } from '../../services/file.service';
import { EMPTY, Observable } from 'rxjs';
import { InboxService } from '../../services/inbox.service';
import { MessageBoxService } from '../shared/message-box/message-box.service';

import { InboxMessage, InboxMessageStatus } from '../../model/inbox-message';
import { Member } from '../../model/member';
import { MemberStateService } from '../../services/member-state.service';
import { InboxAttachment, Signable, SignatureStatus } from '../../model/inbox-attachment';
import { LetterResponse } from '../../model/letter-response';

import { APP_CONFIG } from '../../config/app-config.token';
import { AppConfig } from '../../config/app-config.model';
import { RedirectService } from '../../services/redirect.service';
import { JwtService } from '../../services/jwt.service';
import { UserContextService } from '../../services/user-context.service';
import { PlatformService } from '../../auth/services/platform.service';
import { AnalyticsService } from '../../services/analytics.service';
import { catchError, switchMap, take, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { AtosJwt } from '../../model/atosJwt';
import { AlertComponent } from '../shared/alert/alert.component';
import { LoaderComponent } from '../shared/loader/loader.component';
import { PaginationComponent } from '../shared/pagination/pagination.component';
import { AsyncPipe, DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ViewState, toViewState } from '../../model/view-state';

export type InboxFilter = 'all' | 'unread' | 'read' | 'to-sign';
export type InboxSortDirection = 'desc' | 'asc';

const INBOX_FILTER_STORAGE_KEY = 'myabvv:inbox-filter';
const INBOX_SORT_STORAGE_KEY = 'myabvv:inbox-sort';
const VALID_FILTERS: ReadonlyArray<InboxFilter> = ['all', 'unread', 'read', 'to-sign'];
const VALID_SORT_DIRECTIONS: ReadonlyArray<InboxSortDirection> = ['desc', 'asc'];

@Component({
    selector: 'myabvv-inbox',
    standalone: true,
    templateUrl: './inbox.component.html',
    styleUrls: ['./inbox.component.css'],
    imports: [AlertComponent, LoaderComponent, PaginationComponent, AsyncPipe, DatePipe, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InboxComponent implements OnInit, OnDestroy {
  private fileService = inject(FileService);
  private memberStateService = inject(MemberStateService);
  private inboxService = inject(InboxService);
  private jwtService = inject(JwtService);
  private messageBoxService = inject(MessageBoxService);
  private openTab = inject(RedirectService);
  private userContext = inject(UserContextService);
  private platformService = inject(PlatformService);
  private analyticsService = inject(AnalyticsService);
  private route = inject(ActivatedRoute);
  private config = inject<AppConfig>(APP_CONFIG);

  private destroyRef = inject(DestroyRef);

  state$: Observable<ViewState<InboxMessage[]>>;

  inboxMessageStatus = InboxMessageStatus;
  signatureStatus = SignatureStatus;
  isDownloading = signal(false);
  private member: Member;
  messages = signal<InboxMessage[]>([]);

  // Active filter tab. Initialized from sessionStorage when present, otherwise
  // starts at 'all' as a safe placeholder until messages load. If no stored
  // value exists, applySmartDefaultIfNeeded() may override it once messages
  // are available (see loadMemberAndFetchMessages).
  activeFilter = signal<InboxFilter>(this.readFilterFromStorage() ?? 'all');

  // Tracks whether the filter has been explicitly chosen (either restored
  // from sessionStorage or picked by the user). Prevents the smart default
  // from overriding an explicit choice on subsequent message refreshes.
  private filterExplicitlySet: boolean = this.readFilterFromStorage() !== null;

  // Sort direction for the message list (by datePosted). Defaults to 'desc'
  // (newest first) which matches the existing implicit behavior.
  sortDirection = signal<InboxSortDirection>(this.readSortFromStorage());

  // Messages after applying the active filter and sort direction.
  // Counts are deliberately computed from the unfiltered/unsorted source
  // (this.messages()) so tab badges stay stable regardless of sort.
  filteredMessages = computed(() => {
    const filter = this.activeFilter();
    const direction = this.sortDirection();
    const msgs = this.messages();

    const filtered = (() => {
      switch (filter) {
        case 'unread':
          return msgs.filter(m => m.status === InboxMessageStatus.UNREAD);
        case 'read':
          return msgs.filter(m => m.status === InboxMessageStatus.READ);
        case 'to-sign':
          return msgs.filter(m => this.hasSignableUnsignedAttachment(m));
        case 'all':
        default:
          return msgs;
      }
    })();

    // Copy before sorting to avoid mutating the source signal's array
    return [...filtered].sort((a, b) => {
      const timeA = new Date(a.datePosted).getTime();
      const timeB = new Date(b.datePosted).getTime();
      return direction === 'desc' ? timeB - timeA : timeA - timeB;
    });
  });

  // Per-tab counts used to render badges on the filter bar
  counts = computed(() => {
    const msgs = this.messages();
    return {
      all: msgs.length,
      unread: msgs.filter(m => m.status === InboxMessageStatus.UNREAD).length,
      read: msgs.filter(m => m.status === InboxMessageStatus.READ).length,
      toSign: msgs.filter(m => this.hasSignableUnsignedAttachment(m)).length,
    };
  });

  // Translation key for the empty state when the active filter matches nothing
  emptyFilterMessageKey = computed(() => {
    switch (this.activeFilter()) {
      case 'unread':  return 'INBOX.filter_empty_unread';
      case 'read':    return 'INBOX.filter_empty_read';
      case 'to-sign': return 'INBOX.filter_empty_to_sign';
      default:        return 'INBOX.no_messages';
    }
  });

  // Pagination (derives from filteredMessages so it reacts to filter changes)
  rowsPerPage = 5;
  currentPage = signal(1);
  pages = computed(() => {
    const totalPages = Math.ceil(this.filteredMessages().length / this.rowsPerPage);
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  });
  pagedMessages = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.rowsPerPage;
    return this.filteredMessages().slice(startIndex, startIndex + this.rowsPerPage);
  });

  // Mobile: track signing state for browser return detection
  private signingInProgress = false;
  private signingPollTimer: ReturnType<typeof setInterval> | null = null;
  private appResumeListener: PluginListenerHandle | null = null;

  ngOnInit() {
    this.handleQueryParams();
    this.loadMemberAndFetchMessages();
    this.setupMobileListeners();
  }

  ngOnDestroy() {
    this.stopSigningPoll();
    this.appResumeListener?.remove();
  }

  private loadMemberAndFetchMessages() {
    const isInternalUser = this.userContext.isInternalUser();

    this.state$ = this.memberStateService.getMember().pipe(
      take(1),
      switchMap((member: Member) => {
        this.member = member;

        return isInternalUser
          ? this.inboxService.getInboxMessages({ ssin: member.personalInformation.inss, addAttachment: true })
          : this.inboxService.getMyInboxMessages({ addAttachment: true });
      }),
      tap((inboxMessages: InboxMessage[]) => {
        this.messages.set(inboxMessages);
        this.applySmartDefaultIfNeeded(inboxMessages);
      }),
      toViewState()
    );
  }

  downloadFile(inboxAttachment: InboxAttachment) {
    this.analyticsService.trackEvent('download_inbox_document', {
      mimeType: inboxAttachment.mimeType,
      signable: inboxAttachment.signable
    });

    const attachB64Str = inboxAttachment.attachment.join('');
    const letterResponse = new LetterResponse(inboxAttachment.filename, attachB64Str, inboxAttachment.mimeType, '');
    this.fileService.downloadFile(letterResponse);
    this.markAsRead(inboxAttachment.inboxMessageId);
  }

  markAsRead(msgId: string) {
    if (this.isInternal()) {
      return;
    }

    // External users always use /me endpoint
    this.inboxService.setMyInboxMessageStatus(msgId, InboxMessageStatus.READ.toString())
      .pipe(
        take(1),
        tap((res: InboxMessage) => {
          this.markMessageAsReadInList(res.id);
        }),
        catchError(() => EMPTY)
      ).subscribe();
  }

  onSubmitSignForm(inboxAttachment: InboxAttachment, documentDescription: string) {
    if (this.isInternal()) {
      return;
    }

    const documentType = documentDescription.substring(0, documentDescription.indexOf('-'));

    this.analyticsService.trackEvent('sign_document_initiated', { documentType });

    this.jwtService.getMyToken(inboxAttachment.id.trim(), documentType)
      .pipe(
        take(1),
        tap((jw: AtosJwt) => {
          this.submitSignatureRequest(inboxAttachment, documentType, jw.value);
        }),
        catchError(() => {
          this.analyticsService.trackEvent('sign_document_failed', { documentType });
          return EMPTY;
        })
      ).subscribe();
  }

  private submitSignatureRequest(attachment: InboxAttachment, documentType: string, authToken: string) {
    const xmlContent = this.decodeAttachmentBase64(attachment);
    const resolvedDocType = this.extractDocumentType(xmlContent, documentType);
    const signaturePayload = this.extractSignaturePayload(xmlContent);

    const targetUrl = this.buildSignatureReturnUrl(attachment.id.trim(), resolvedDocType);

    this.signingInProgress = true;
    if (this.platformService.isMobile()) {
      this.startSigningPoll();
    }

    this.openTab.post({
      SignatureRequest: btoa(signaturePayload),
      adesLevel: 'LEVEL_LT',
      application: this.config.atosC1AppName,
      applicationAuthToken: authToken,
      expectedSigner: this.member.personalInformation.inss,
      locale: this.member.personalInformation.motherLanguage === 'F' ? 'fr-be' : 'nl-be',
      target: targetUrl,
      signaturePackaging: 'ENVELOPED',
      signatureFormat: 'XMLDSIG',
      returnSignedDocument: true
    }, this.config.atosSmartSignXmlUrl, '_self', 'xml');

    this.markAsRead(attachment.inboxMessageId);
  }

  private decodeAttachmentBase64(attachment: InboxAttachment): string {
    const base64 = attachment.attachment.join('').replace(/\s/g, '');
    return window.atob(base64);
  }

  private extractDocumentType(xmlContent: string, fallback: string): string {
    const match = xmlContent.match(/<DocumentType>([^<]+)<\/DocumentType>/);
    return match?.[1]?.trim() ?? fallback.trim();
  }

  private extractSignaturePayload(xmlContent: string): string {
    const innerBase64 = xmlContent.substring(
      xmlContent.indexOf('<XMLDocument>') + '<XMLDocument>'.length,
      xmlContent.lastIndexOf('</XMLDocument>')
    );
    return window.atob(innerBase64.replace(/\s/g, ''));
  }

  private buildSignatureReturnUrl(attachmentId: string, docType: string): string {
    const context = this.platformService.isMobile()
      ? 'mobile'
      : (this.userContext.isInternalUser() ? 'internal' : 'external');
    const docPath = docType ? `${encodeURIComponent(docType)}/` : '';
    const inss = this.member.personalInformation.inss;

    // MAF-660: no client_id/client_secret query any more. Nothing in the return path
    // reads them (Tyk B2B route is keyless, consumer/forms ignore them); pre test confirms.
    return `${this.config.atosSmartSignReturnUrl}${inss}/signed-documents/atos/${docPath}${attachmentId}/${context}`;
  }

  hasData() {
    return this.messages()?.length > 0;
  }

  isInternal() {
    return this.userContext.isInternalUser();
  }

  // Collapses the full backend status set (legacy DB2 rows included) onto the
  // three badge states the template renders: OK is a successful signature, and
  // the Atos error codes all render as a generic signing error.
  displayStatus(attachment: InboxAttachment): SignatureStatus {
    switch (attachment.signatureStatus) {
      case SignatureStatus.OK:
        return SignatureStatus.SIGNED;
      case SignatureStatus.USER_CANCELLED:
      case SignatureStatus.CRL_ERROR:
      case SignatureStatus.OCSP_ERROR:
      case SignatureStatus.SIGNER_NOT_EXPECTED_ONE:
      case SignatureStatus.APP_ERROR:
        return SignatureStatus.ERROR;
      default:
        return attachment.signatureStatus;
    }
  }

  needSignature(attachment: InboxAttachment) {
    return attachment.signatureStatus === SignatureStatus.UNSIGNED && attachment.signable === Signable.Y && !this.isInternal();
  }

  isSignableDocument(attachment: InboxAttachment) {
    return !(attachment.signatureStatus === SignatureStatus.UNSIGNED && attachment.signable === Signable.N);
  }

  private setupMobileListeners(): void {
    if (!this.platformService.isMobile()) {
      return;
    }

    // Immediate refresh when user switches back to the app from Safari
    App.addListener('resume', () => {
      if (this.signingInProgress) {
        this.refreshAndCheckSignature();
      }
    }).then(handle => this.appResumeListener = handle);
  }

  private startSigningPoll(): void {
    this.stopSigningPoll();
    this.signingPollTimer = setInterval(() => {
      this.refreshAndCheckSignature();
    }, 5000);
  }

  private stopSigningPoll(): void {
    if (this.signingPollTimer) {
      clearInterval(this.signingPollTimer);
      this.signingPollTimer = null;
    }
  }

  private refreshAndCheckSignature(): void {
    const previousStatuses = new Map(
      this.messages().flatMap(m => (m.attachments || []).map(a => [a.id.trim(), a.signatureStatus] as const))
    );

    const isInternalUser = this.userContext.isInternalUser();
    const messages$ = isInternalUser
      ? this.inboxService.getInboxMessages({ ssin: this.member.personalInformation.inss, addAttachment: true })
      : this.inboxService.getMyInboxMessages({ addAttachment: true });

    messages$.pipe(
      take(1),
      tap((inboxMessages: InboxMessage[]) => {
        this.messages.set(inboxMessages);

        const newlySigned = inboxMessages.some(m =>
          (m.attachments || []).some(a =>
            a.signatureStatus === SignatureStatus.SIGNED &&
            previousStatuses.get(a.id.trim()) === SignatureStatus.UNSIGNED
          )
        );

        if (newlySigned) {
          this.signingInProgress = false;
          this.stopSigningPoll();
          this.analyticsService.trackEvent('sign_document_success');
          this.showPopup();
        }
      }),
      catchError(() => EMPTY)
    ).subscribe();
  }

  private showPopup(): void {
    this.messageBoxService.showModal(
      'INBOX.signed_dialog_title',
      'INBOX.signed_success',
      'INBOX.dialog_ok',
      '',
      '/unemployment/inbox');
  }

  private handleQueryParams(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          if (params['status'] === 'success') {
            this.analyticsService.trackEvent('sign_document_success');
            this.showPopup();
          }
        },
        error: err => console.error('[InboxComponent] Query params error:', err)
      });
  }

  // Optimistically flips a single message's status to READ in the local list.
  // The filter counts (counts()) auto-update via their computed signal dependency.
  private markMessageAsReadInList(messageId: string): void {
    this.messages.update(msgs => {
      const index = msgs.findIndex(message => message.id === messageId);
      if (index === -1) {
        return msgs;
      }
      const updated = [...msgs];
      updated[index] = { ...updated[index], status: InboxMessageStatus.READ };
      return updated;
    });
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
  }

  onFilterChange(filter: InboxFilter) {
    this.activeFilter.set(filter);
    // Any manual change counts as an explicit choice and disables the smart default
    this.filterExplicitlySet = true;
    // Reset to first page so the user doesn't land on an empty page after narrowing the result set
    this.currentPage.set(1);
    this.writeFilterToStorage(filter);
  }

  onSortChange(direction: InboxSortDirection) {
    if (this.sortDirection() === direction) {
      return;
    }
    this.sortDirection.set(direction);
    // Reset to first page so ordering changes land the user at the top of the list
    this.currentPage.set(1);
    this.writeSortToStorage(direction);
  }

  // A message belongs to the "to-sign" filter if it has at least one signable
  // attachment that is still in the UNSIGNED state. Per business rule, a message
  // never contains more than one signable document, but we guard against the
  // attachments array being missing.
  private hasSignableUnsignedAttachment(message: InboxMessage): boolean {
    return (message.attachments || []).some(a =>
      a.signable === Signable.Y &&
      a.signatureStatus === SignatureStatus.UNSIGNED
    );
  }

  // Returns the stored filter value, or null if no valid value was found.
  // The null return is what allows the smart default to kick in on first load.
  private readFilterFromStorage(): InboxFilter | null {
    try {
      const stored = sessionStorage.getItem(INBOX_FILTER_STORAGE_KEY);
      if (stored && (VALID_FILTERS as ReadonlyArray<string>).includes(stored)) {
        return stored as InboxFilter;
      }
    } catch {
      // sessionStorage may be unavailable (private mode, SSR, etc.)
    }
    return null;
  }

  // Applied once per session after messages are loaded, only if the user has
  // not explicitly picked a filter (i.e. nothing was stored in sessionStorage).
  // Forces the "unread" view when there are unread messages so members don't
  // miss them, while still falling back to "all" when the inbox is fully read
  // (avoids landing on a misleading empty state).
  private applySmartDefaultIfNeeded(inboxMessages: InboxMessage[]): void {
    if (this.filterExplicitlySet) {
      return;
    }

    const hasUnread = inboxMessages.some(m => m.status === InboxMessageStatus.UNREAD);
    this.activeFilter.set(hasUnread ? 'unread' : 'all');
    this.filterExplicitlySet = true;
  }

  private writeFilterToStorage(filter: InboxFilter): void {
    try {
      sessionStorage.setItem(INBOX_FILTER_STORAGE_KEY, filter);
    } catch {
      // Best-effort persistence; silently ignore storage errors
    }
  }

  private readSortFromStorage(): InboxSortDirection {
    try {
      const stored = sessionStorage.getItem(INBOX_SORT_STORAGE_KEY);
      if (stored && (VALID_SORT_DIRECTIONS as ReadonlyArray<string>).includes(stored)) {
        return stored as InboxSortDirection;
      }
    } catch {
      // sessionStorage may be unavailable (private mode, SSR, etc.)
    }
    return 'desc';
  }

  private writeSortToStorage(direction: InboxSortDirection): void {
    try {
      sessionStorage.setItem(INBOX_SORT_STORAGE_KEY, direction);
    } catch {
      // Best-effort persistence; silently ignore storage errors
    }
  }

}
